import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
    Box, Grid, Paper, Typography, Stack,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    CircularProgress, Card, CardContent, Tabs, Tab, Button,
    TextField, InputAdornment, Chip,
    useTheme, useMediaQuery
} from '@mui/material';
import AssessmentIcon from '@mui/icons-material/Assessment';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import {
    Print as PrintIcon,
    Search as SearchIcon,
    ReceiptLong as ReceiptLongIcon,
    History as HistoryIcon,
    Speed as SpeedIcon,
    AccountBalanceWallet as AccountBalanceWalletIcon,
} from '@mui/icons-material';
import axios from 'axios';
import { useReactToPrint } from 'react-to-print';
import { getApiUrl } from '../config/api';
import {
    LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
    Cell, PieChart, Pie, Legend
} from 'recharts';

const ReportsPage = () => {
    const [tabValue, setTabValue] = useState(0);
    const [loading, setLoading] = useState(true);
    const [liquidityData, setLiquidityData] = useState<any>(null);
    const [velocityData, setVelocityData] = useState<any[]>([]);
    const [agedData, setAgedData] = useState<any[]>([]);
    const [trialBalanceData, setTrialBalanceData] = useState<any[]>([]);
    const [operatingPosition, setOperatingPosition] = useState<any>(null);
    const [incomeByType, setIncomeByType] = useState<any[]>([]);
    const [studentPassport, setStudentPassport] = useState<any>(null);
    const [balanceSheet, setBalanceSheet] = useState<any>(null);
    const [incomeStatement, setIncomeStatement] = useState<any>(null);
    const [searchStudentId, setSearchStudentId] = useState('');

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const isSmallMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const [permissions, setPermissions] = useState<string[]>([]);

    const componentRef = useRef<HTMLDivElement>(null);
    const handlePrint = useReactToPrint({
        contentRef: componentRef,
    });

    const fetchAllReports = useCallback(async () => {
        try {
            setLoading(true);
            const userInfo = localStorage.getItem('userInfo');
            const token = userInfo ? JSON.parse(userInfo).token : null;
            const config = { headers: { Authorization: `Bearer ${token}` } };

            const financialBaseUrl = getApiUrl('financial-reports');
            const reportsBaseUrl = getApiUrl('reports');

            const [liquidRes, velRes, agedRes, tbRes, opRes, incRes, bsRes, isRes] = await Promise.all([
                axios.get(`${financialBaseUrl}/liquidity`, config),
                axios.get(`${financialBaseUrl}/velocity`, config),
                axios.get(`${financialBaseUrl}/aged-receivables`, config),
                axios.get(`${financialBaseUrl}/trial-balance`, config),
                axios.get(`${financialBaseUrl}/operating-position`, config),
                axios.get(`${financialBaseUrl}/income-by-fee-type`, config),
                axios.get(`${reportsBaseUrl}/balance-sheet`, config),
                axios.get(`${reportsBaseUrl}/income-statement`, config)
            ]);

            setLiquidityData(liquidRes.data);
            setVelocityData(velRes.data);
            setAgedData(agedRes.data);
            setTrialBalanceData(tbRes.data);
            setOperatingPosition(opRes.data);
            setIncomeByType(incRes.data);
            setBalanceSheet(bsRes.data);
            setIncomeStatement(isRes.data);
        } catch (error) {
            console.error('Fetch reports error:', error);
        } finally {
            setLoading(false);
        }
    }, []);

    const fetchStudentPassport = async () => {
        if (!searchStudentId) return;
        try {
            const userInfo = localStorage.getItem('userInfo');
            const token = userInfo ? JSON.parse(userInfo).token : null;
            const config = { headers: { Authorization: `Bearer ${token}` } };
            const res = await axios.get(getApiUrl(`financial-reports/student-passport/${searchStudentId}`), config);
            setStudentPassport(res.data);
        } catch (error) {
            console.error('Fetch passport error:', error);
            alert('Student not found or error fetching data');
        }
    };

    useEffect(() => {
        const userInfo = localStorage.getItem('userInfo');
        if (userInfo) {
            const parsed = JSON.parse(userInfo);
            const userObj = parsed.user || parsed;
            const perms = userObj.permissions || [];
            setPermissions(perms);
            
            if (perms.includes('view_reports')) {
                fetchAllReports();
            }
        }
    }, [fetchAllReports]);

    const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
        setTabValue(newValue);
    };

    if (loading && permissions.includes('view_reports')) return <Box sx={{ display: 'flex', justifyContent: 'center', p: 10 }}><CircularProgress /></Box>;

    if (!permissions.includes('view_reports')) {
        return (
            <Box sx={{ p: 4, textAlign: 'center' }}>
                <Typography variant="h5" color="error" fontWeight={700}>Access Denied</Typography>
                <Typography variant="body1" color="text.secondary" sx={{ mt: 2 }}>
                    You do not have permission to view financial intelligence reports.
                </Typography>
            </Box>
        );
    }

    const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8'];

    return (
        <Box sx={{ p: isMobile ? 2 : 4, bgcolor: '#F4F7FE', minHeight: 'calc(100vh - 64px)' }}>
            {/* Header Content */}
            <Stack direction={isMobile ? "column" : "row"} justifyContent="space-between" alignItems={isMobile ? "flex-start" : "center"} mb={4} spacing={2}>
                <Box>
                    <Typography variant={isMobile ? "h5" : "h4"} fontWeight={800} color="#263B7C">Financial Intelligence</Typography>
                    <Typography variant="body2" color="text.secondary">Corporate-level insights and fiscal health monitoring</Typography>
                </Box>
                <Button
                    fullWidth={isSmallMobile}
                    startIcon={<PrintIcon />}
                    variant="contained"
                    sx={{ bgcolor: '#263B7C', borderRadius: '12px', px: 3, textTransform: 'none' }}
                    onClick={() => handlePrint()}
                >
                    Export Report
                </Button>
            </Stack>

            <Tabs
                value={tabValue}
                onChange={handleTabChange}
                variant="scrollable"
                scrollButtons="auto"
                sx={{ mb: 4, '& .MuiTab-root': { fontWeight: 600, textTransform: 'none', minHeight: '48px', fontSize: isSmallMobile ? '0.8rem' : 'inherit' } }}
            >
                <Tab label="Performance" icon={<SpeedIcon />} iconPosition="start" />
                <Tab label="Balance Sheet" icon={<AccountBalanceWalletIcon />} iconPosition="start" />
                <Tab label="Income Statement" icon={<ReceiptLongIcon />} iconPosition="start" />
                <Tab label="Payment Velocity" icon={<TrendingUpIcon />} iconPosition="start" />
                <Tab label="Debt Aging" icon={<HistoryIcon />} iconPosition="start" />
                <Tab label="Financial Passport" icon={<AccountBalanceIcon />} iconPosition="start" />
                <Tab label="Integrity Check" icon={<AssessmentIcon />} iconPosition="start" />
            </Tabs>

            <Box ref={componentRef}>
                {tabValue === 0 && (
                    <Grid container spacing={isMobile ? 2 : 3}>
                        {/* Summary Cards */}
                        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                            <Card sx={{ borderRadius: '16px', border: '1px solid #edf2f7', boxShadow: 'none' }}>
                                <CardContent sx={{ p: isSmallMobile ? 2 : 3 }}>
                                    <Stack direction="row" spacing={2} alignItems="center">
                                        <Box sx={{ p: 1.5, bgcolor: '#EBF8FF', borderRadius: '12px' }}><AccountBalanceIcon color="primary" fontSize="small" /></Box>
                                        <Box>
                                            <Typography variant="caption" color="text.secondary" fontWeight={700}>TOTAL LIQUIDITY</Typography>
                                            <Typography variant={isSmallMobile ? "h6" : "h5"} fontWeight={800} color="#263B7C">${liquidityData?.totalLiquidity.toLocaleString()}</Typography>
                                        </Box>
                                    </Stack>
                                </CardContent>
                            </Card>
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                            <Card sx={{ borderRadius: '16px', border: '1px solid #edf2f7', boxShadow: 'none' }}>
                                <CardContent sx={{ p: isSmallMobile ? 2 : 3 }}>
                                    <Stack direction="row" spacing={2} alignItems="center">
                                        <Box sx={{ p: 1.5, bgcolor: '#E6FFFA', borderRadius: '12px' }}><TrendingUpIcon sx={{ color: '#38A169' }} fontSize="small" /></Box>
                                        <Box>
                                            <Typography variant="caption" color="text.secondary" fontWeight={700}>NET OPERATING POSITION</Typography>
                                            <Typography variant={isSmallMobile ? "h6" : "h5"} fontWeight={800} color="#38A169">${operatingPosition?.netPosition.toLocaleString()}</Typography>
                                        </Box>
                                    </Stack>
                                </CardContent>
                            </Card>
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                            <Card sx={{ borderRadius: '16px', border: '1px solid #edf2f7', boxShadow: 'none' }}>
                                <CardContent sx={{ p: isSmallMobile ? 2 : 3 }}>
                                    <Stack direction="row" spacing={2} alignItems="center">
                                        <Box sx={{ p: 1.5, bgcolor: '#FFF5F5', borderRadius: '12px' }}><AssessmentIcon sx={{ color: '#E53E3E' }} fontSize="small" /></Box>
                                        <Box>
                                            <Typography variant="caption" color="text.secondary" fontWeight={700}>OPERATING MARGIN</Typography>
                                            <Typography variant={isSmallMobile ? "h6" : "h5"} fontWeight={800} color="#E53E3E">{operatingPosition?.margin.toFixed(1)}%</Typography>
                                        </Box>
                                    </Stack>
                                </CardContent>
                            </Card>
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                            <Card sx={{ borderRadius: '16px', border: '1px solid #edf2f7', boxShadow: 'none' }}>
                                <CardContent sx={{ p: isSmallMobile ? 2 : 3 }}>
                                    <Stack direction="row" spacing={2} alignItems="center">
                                        <Box sx={{ p: 1.5, bgcolor: '#F0EFFF', borderRadius: '12px' }}><TrendingUpIcon sx={{ color: '#667EEA' }} fontSize="small" /></Box>
                                        <Box>
                                            <Typography variant="caption" color="text.secondary" fontWeight={700}>GROSS REVENUE</Typography>
                                            <Typography variant={isSmallMobile ? "h6" : "h5"} fontWeight={800} color="#667EEA">${operatingPosition?.income.toLocaleString()}</Typography>
                                        </Box>
                                    </Stack>
                                </CardContent>
                            </Card>
                        </Grid>

                        <Grid size={{ xs: 12, md: 6 }}>
                            <Paper sx={{ p: isSmallMobile ? 2 : 3, borderRadius: '20px', border: '1px solid #edf2f7', boxShadow: 'none', height: '100%' }}>
                                <Typography variant="h6" fontWeight={700} mb={3}>Revenue by Fee Type</Typography>
                                {incomeByType && incomeByType.length > 0 && (
                                    <ResponsiveContainer width="100%" height={isSmallMobile ? 300 : 350}>
                                        <PieChart>
                                            <Pie
                                                data={incomeByType}
                                                cx="50%"
                                                cy="50%"
                                                innerRadius={isSmallMobile ? 40 : 60}
                                                outerRadius={isSmallMobile ? 80 : 100}
                                                paddingAngle={5}
                                                dataKey="total"
                                                nameKey="name"
                                                label={!isSmallMobile ? ({ name, payload }: any) => `${name}: $${payload.total.toLocaleString()}` : false}
                                            >
                                                {incomeByType.map((entry, index) => (
                                                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                                ))}
                                            </Pie>
                                            <Tooltip />
                                            {isSmallMobile && <Legend verticalAlign="bottom" height={36} wrapperStyle={{ fontSize: '0.75rem' }} />}
                                        </PieChart>
                                    </ResponsiveContainer>
                                )}
                            </Paper>
                        </Grid>

                        <Grid size={{ xs: 12, md: 6 }}>
                            <Paper sx={{ p: isSmallMobile ? 2 : 3, borderRadius: '20px', border: '1px solid #edf2f7', boxShadow: 'none', height: '100%' }}>
                                <Typography variant="h6" fontWeight={700} mb={3}>Liquidity Overview</Typography>
                                <TableContainer sx={{ overflowX: 'auto' }}>
                                    <Table size="small">
                                        <TableHead>
                                            <TableRow>
                                                <TableCell sx={{ fontWeight: 700 }}>Account</TableCell>
                                                <TableCell align="right" sx={{ fontWeight: 700 }}>Balance</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {liquidityData?.accounts.map((acc: any) => (
                                                <TableRow key={acc.id}>
                                                    <TableCell>{acc.name}</TableCell>
                                                    <TableCell align="right" sx={{ fontWeight: 600 }}>${Number(acc.balance).toLocaleString()}</TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </Paper>
                        </Grid>
                    </Grid>
                )}

                {tabValue === 1 && (
                    <Grid container spacing={isMobile ? 2 : 3}>
                        <Grid size={{ xs: 12, md: 6 }}>
                            <Paper sx={{ p: isSmallMobile ? 2 : 3, borderRadius: '20px', border: '1px solid #edf2f7', boxShadow: 'none' }}>
                                <Typography variant="h6" fontWeight={800} color="#263B7C" mb={3}>ASSETS</Typography>
                                <TableContainer sx={{ overflowX: 'auto' }}>
                                    <Table size="small">
                                        <TableBody>
                                            {balanceSheet?.assets.map((acc: any) => (
                                                <TableRow key={acc.id}>
                                                    <TableCell>{acc.name}</TableCell>
                                                    <TableCell align="right" sx={{ fontWeight: 600 }}>${Number(acc.balance).toLocaleString()}</TableCell>
                                                </TableRow>
                                            ))}
                                            <TableRow sx={{ bgcolor: '#F7FAFC' }}>
                                                <TableCell sx={{ fontWeight: 800 }}>TOTAL ASSETS</TableCell>
                                                <TableCell align="right" sx={{ fontWeight: 900, color: '#263B7C' }}>
                                                    ${balanceSheet?.totals.assets.toLocaleString()}
                                                </TableCell>
                                            </TableRow>
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </Paper>
                        </Grid>
                        <Grid size={{ xs: 12, md: 6 }}>
                            <Stack spacing={3}>
                                <Paper sx={{ p: isSmallMobile ? 2 : 3, borderRadius: '20px', border: '1px solid #edf2f7', boxShadow: 'none' }}>
                                    <Typography variant="h6" fontWeight={800} color="#E53E3E" mb={3}>LIABILITIES</Typography>
                                    <TableContainer sx={{ overflowX: 'auto' }}>
                                        <Table size="small">
                                            <TableBody>
                                                {balanceSheet?.liabilities.map((acc: any) => (
                                                    <TableRow key={acc.id}>
                                                        <TableCell>{acc.name}</TableCell>
                                                        <TableCell align="right" sx={{ fontWeight: 600 }}>${Number(acc.balance).toLocaleString()}</TableCell>
                                                    </TableRow>
                                                ))}
                                                {balanceSheet?.liabilities.length === 0 && (
                                                    <TableRow><TableCell colSpan={2} sx={{ color: 'text.secondary', fontStyle: 'italic' }}>No commercial liabilities</TableCell></TableRow>
                                                )}
                                                <TableRow sx={{ bgcolor: '#FFF5F5' }}>
                                                    <TableCell sx={{ fontWeight: 800 }}>TOTAL LIABILITIES</TableCell>
                                                    <TableCell align="right" sx={{ fontWeight: 900, color: '#E53E3E' }}>
                                                        ${balanceSheet?.totals.liabilities.toLocaleString()}
                                                    </TableCell>
                                                </TableRow>
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                </Paper>
                                <Paper sx={{ p: isSmallMobile ? 2 : 3, borderRadius: '20px', border: '1px solid #edf2f7', boxShadow: 'none' }}>
                                    <Typography variant="h6" fontWeight={800} color="#805AD5" mb={3}>EQUITY</Typography>
                                    <TableContainer sx={{ overflowX: 'auto' }}>
                                        <Table size="small">
                                            <TableBody>
                                                {balanceSheet?.equity.map((acc: any) => (
                                                    <TableRow key={acc.id}>
                                                        <TableCell>{acc.name}</TableCell>
                                                        <TableCell align="right" sx={{ fontWeight: 600 }}>${Number(acc.balance).toLocaleString()}</TableCell>
                                                    </TableRow>
                                                ))}
                                                <TableRow sx={{ bgcolor: '#FAF5FF' }}>
                                                    <TableCell sx={{ fontWeight: 800 }}>TOTAL EQUITY</TableCell>
                                                    <TableCell align="right" sx={{ fontWeight: 900, color: '#805AD5' }}>
                                                        ${balanceSheet?.totals.equity.toLocaleString()}
                                                    </TableCell>
                                                </TableRow>
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                </Paper>
                            </Stack>
                        </Grid>
                    </Grid>
                )}

                {tabValue === 2 && (
                    <Paper sx={{ p: isSmallMobile ? 2 : 4, borderRadius: '20px', border: '1px solid #edf2f7', boxShadow: 'none' }}>
                        <Typography variant={isSmallMobile ? "h6" : "h5"} fontWeight={800} color="#263B7C" align="center" mb={4}>Statement of Comprehensive Income</Typography>
                        <Grid container spacing={4}>
                            {/* Revenue Section */}
                            <Grid size={{ xs: 12, md: 6 }}>
                                <Typography variant="h6" fontWeight={700} sx={{ borderBottom: '2px solid #38A169', pb: 1, mb: 2 }}>Revenue</Typography>
                                <TableContainer sx={{ overflowX: 'auto' }}>
                                    <Table size="small">
                                        <TableBody>
                                            {incomeStatement?.income.map((acc: any) => (
                                                <TableRow key={acc.id}>
                                                    <TableCell>{acc.name}</TableCell>
                                                    <TableCell align="right" sx={{ fontWeight: 600 }}>${Number(acc.balance).toLocaleString()}</TableCell>
                                                </TableRow>
                                            ))}
                                            <TableRow sx={{ bgcolor: '#E6FFFA' }}>
                                                <TableCell sx={{ fontWeight: 800 }}>TOTAL REVENUE</TableCell>
                                                <TableCell align="right" sx={{ fontWeight: 900, color: '#38A169' }}>
                                                    ${incomeStatement?.totals.income.toLocaleString()}
                                                </TableCell>
                                            </TableRow>
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </Grid>

                            {/* COGS Section */}
                            <Grid size={{ xs: 12, md: 6 }}>
                                <Typography variant="h6" fontWeight={700} sx={{ borderBottom: '2px solid #DD6B20', pb: 1, mb: 2 }}>Cost of Goods Sold</Typography>
                                <TableContainer sx={{ overflowX: 'auto' }}>
                                    <Table size="small">
                                        <TableBody>
                                            {incomeStatement?.cogs?.map((acc: any) => (
                                                <TableRow key={acc.id}>
                                                    <TableCell>{acc.name}</TableCell>
                                                    <TableCell align="right" sx={{ fontWeight: 600 }}>${Number(acc.balance).toLocaleString()}</TableCell>
                                                </TableRow>
                                            ))}
                                            {(!incomeStatement?.cogs || incomeStatement.cogs.length === 0) && (
                                                <TableRow><TableCell colSpan={2} sx={{ color: 'text.secondary', fontStyle: 'italic' }}>No COGS recorded</TableCell></TableRow>
                                            )}
                                            <TableRow sx={{ bgcolor: '#FFFAF0' }}>
                                                <TableCell sx={{ fontWeight: 800 }}>TOTAL COGS</TableCell>
                                                <TableCell align="right" sx={{ fontWeight: 900, color: '#DD6B20' }}>
                                                    ${incomeStatement?.totals.cogs?.toLocaleString() || '0'}
                                                </TableCell>
                                            </TableRow>
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </Grid>

                            {/* Gross Profit Summary */}
                            <Grid size={{ xs: 12 }}>
                                <Box sx={{ p: 2, bgcolor: '#F0FFF4', borderRadius: '12px', border: '1px solid #C6F6D5', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <Typography variant="h6" fontWeight={800} color="#2F855A">GROSS PROFIT</Typography>
                                    <Typography variant="h5" fontWeight={900} color="#2F855A">${incomeStatement?.totals.grossProfit?.toLocaleString() || '0'}</Typography>
                                </Box>
                            </Grid>

                            {/* Operating Expenses Section */}
                            <Grid size={{ xs: 12 }}>
                                <Typography variant="h6" fontWeight={700} sx={{ borderBottom: '2px solid #E53E3E', pb: 1, mb: 2, mt: 2 }}>Operating Expenses</Typography>
                                <TableContainer sx={{ overflowX: 'auto' }}>
                                    <Table size="small">
                                        <TableBody>
                                            {incomeStatement?.expenses.map((acc: any) => (
                                                <TableRow key={acc.id}>
                                                    <TableCell>{acc.name}</TableCell>
                                                    <TableCell align="right" sx={{ fontWeight: 600 }}>${Number(acc.balance).toLocaleString()}</TableCell>
                                                </TableRow>
                                            ))}
                                            {incomeStatement?.expenses.length === 0 && (
                                                <TableRow><TableCell colSpan={2} sx={{ color: 'text.secondary', fontStyle: 'italic' }}>No operating expenses recorded</TableCell></TableRow>
                                            )}
                                            <TableRow sx={{ bgcolor: '#FFF5F5' }}>
                                                <TableCell sx={{ fontWeight: 800 }}>TOTAL OPERATING EXPENSES</TableCell>
                                                <TableCell align="right" sx={{ fontWeight: 900, color: '#E53E3E' }}>
                                                    ${incomeStatement?.totals.expenses.toLocaleString()}
                                                </TableCell>
                                            </TableRow>
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </Grid>

                            {/* Net Income Summary */}
                            <Grid size={{ xs: 12 }}>
                                <Box sx={{ mt: 2, p: isSmallMobile ? 2 : 3, bgcolor: '#263B7C', borderRadius: '16px', color: 'white', display: 'flex', flexDirection: isSmallMobile ? 'column' : 'row', justifyContent: 'space-between', alignItems: isSmallMobile ? 'flex-start' : 'center', gap: isSmallMobile ? 1 : 0 }}>
                                    <Typography variant={isSmallMobile ? "subtitle1" : "h5"} fontWeight={800}>NET OPERATING POSITION</Typography>
                                    <Typography variant={isSmallMobile ? "h5" : "h4"} fontWeight={900}>${incomeStatement?.totals.netIncome.toLocaleString()}</Typography>
                                </Box>
                            </Grid>
                        </Grid>
                    </Paper>
                )}

                {tabValue === 3 && (
                    <Paper sx={{ p: isSmallMobile ? 2 : 3, borderRadius: '20px', border: '1px solid #edf2f7', boxShadow: 'none' }}>
                        <Typography variant="h6" fontWeight={700} mb={3}>Payment Velocity Trends</Typography>
                        {velocityData && velocityData.length > 0 && (
                            <ResponsiveContainer width="100%" height={isSmallMobile ? 300 : 400}>
                                <LineChart data={velocityData}>
                                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                                    <XAxis dataKey="date" style={{ fontSize: '0.75rem' }} />
                                    <YAxis style={{ fontSize: '0.75rem' }} />
                                    <Tooltip />
                                    <Line type="monotone" dataKey="amount" stroke="#263B7C" strokeWidth={3} dot={{ r: 4, fill: '#263B7C' }} activeDot={{ r: 8 }} />
                                </LineChart>
                            </ResponsiveContainer>
                        )}
                    </Paper>
                )}

                {tabValue === 4 && (
                    <Paper sx={{ p: isSmallMobile ? 2 : 3, borderRadius: '20px', border: '1px solid #edf2f7', boxShadow: 'none' }}>
                        <Typography variant="h6" fontWeight={700} mb={3}>Aged Receivables Professional Ledger</Typography>
                        <TableContainer sx={{ overflowX: 'auto' }}>
                            <Table sx={{ minWidth: isMobile ? 800 : 'auto' }}>
                                <TableHead>
                                    <TableRow>
                                        <TableCell sx={{ fontWeight: 700 }}>Student Name</TableCell>
                                        <TableCell sx={{ fontWeight: 700 }}>Invoice #</TableCell>
                                        <TableCell sx={{ fontWeight: 700 }}>Due Date</TableCell>
                                        <TableCell align="center" sx={{ fontWeight: 700 }}>Days Overdue</TableCell>
                                        <TableCell align="center" sx={{ fontWeight: 700 }}>Aging Category</TableCell>
                                        <TableCell align="right" sx={{ fontWeight: 700 }}>Balance Due</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {agedData.map((row: any, index: number) => (
                                        <TableRow key={index} hover>
                                            <TableCell sx={{ fontWeight: 600 }}>{row.studentName}</TableCell>
                                            <TableCell>{row.invoiceNo}</TableCell>
                                            <TableCell>{new Date(row.dueDate).toLocaleDateString()}</TableCell>
                                            <TableCell align="center">
                                                <Chip
                                                    label={row.daysOverdue}
                                                    color={row.daysOverdue > 60 ? 'error' : (row.daysOverdue > 30 ? 'warning' : 'default')}
                                                    size="small"
                                                />
                                            </TableCell>
                                            <TableCell align="center">{row.category} Days</TableCell>
                                            <TableCell align="right" sx={{ fontWeight: 700, color: '#263B7C' }}>${row.amount.toLocaleString()}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Paper>
                )}

                {tabValue === 5 && (
                    <Box>
                        <Paper sx={{ p: isSmallMobile ? 2 : 4, borderRadius: '20px', mb: 3, border: '1px solid #edf2f7', boxShadow: 'none' }}>
                            <Grid container spacing={3} alignItems="center">
                                <Grid size={{ xs: 12, md: 8 }}>
                                    <Typography variant="h6" fontWeight={700} mb={1}>Student Financial Passport</Typography>
                                    <Typography variant="body2" color="text.secondary">Enter a Student ID to view their 360-degree financial history</Typography>
                                </Grid>
                                <Grid size={{ xs: 12, md: 4 }}>
                                    <TextField
                                        fullWidth
                                        placeholder="Enter Student ID..."
                                        value={searchStudentId}
                                        onChange={(e) => setSearchStudentId(e.target.value)}
                                        onKeyPress={(e) => e.key === 'Enter' && fetchStudentPassport()}
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <SearchIcon fontSize="small" />
                                                </InputAdornment>
                                            ),
                                            endAdornment: (
                                                <InputAdornment position="end">
                                                    <Button onClick={fetchStudentPassport} sx={{ fontWeight: 700, textTransform: 'none' }}>Search</Button>
                                                </InputAdornment>
                                            ),
                                            sx: { borderRadius: '12px' }
                                        }}
                                    />
                                </Grid>
                            </Grid>
                        </Paper>

                        {studentPassport && (
                            <Box>
                                <Grid container spacing={isMobile ? 2 : 3} mb={3}>
                                    <Grid size={{ xs: 12, sm: 4 }}>
                                        <Paper sx={{ p: isSmallMobile ? 2 : 3, borderRadius: '20px', bgcolor: '#F0FFF4', border: '1px solid #C6F6D5' }}>
                                            <Typography variant="caption" color="success.main" fontWeight={700}>Total Invoiced</Typography>
                                            <Typography variant={isSmallMobile ? "h5" : "h4"} fontWeight={900}>${studentPassport.summary.totalInvoiced.toLocaleString()}</Typography>
                                        </Paper>
                                    </Grid>
                                    <Grid size={{ xs: 12, sm: 4 }}>
                                        <Paper sx={{ p: isSmallMobile ? 2 : 3, borderRadius: '20px', bgcolor: '#EBF8FF', border: '1px solid #BEE3F8' }}>
                                            <Typography variant="caption" color="primary" fontWeight={700}>Total Paid</Typography>
                                            <Typography variant={isSmallMobile ? "h5" : "h4"} fontWeight={900}>${studentPassport.summary.totalPaid.toLocaleString()}</Typography>
                                        </Paper>
                                    </Grid>
                                    <Grid size={{ xs: 12, sm: 4 }}>
                                        <Paper sx={{ p: isSmallMobile ? 2 : 3, borderRadius: '20px', bgcolor: '#FFF5F5', border: '1px solid #FED7D7' }}>
                                            <Typography variant="caption" color="error" fontWeight={700}>Current Balance</Typography>
                                            <Typography variant={isSmallMobile ? "h5" : "h4"} fontWeight={900} color="error">${studentPassport.summary.outstandingBalance.toLocaleString()}</Typography>
                                        </Paper>
                                    </Grid>
                                </Grid>

                                <Paper sx={{ p: isSmallMobile ? 2 : 3, borderRadius: '20px', border: '1px solid #edf2f7', boxShadow: 'none' }}>
                                    <Typography variant="h6" fontWeight={800} mb={3}>Complete Transaction History</Typography>
                                    <TableContainer sx={{ overflowX: 'auto' }}>
                                        <Table sx={{ minWidth: isSmallMobile ? 600 : 'auto' }}>
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell>Date</TableCell>
                                                    <TableCell>Reference</TableCell>
                                                    <TableCell>Description</TableCell>
                                                    <TableCell align="right">Amount</TableCell>
                                                    <TableCell align="center">Status</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {studentPassport.history.map((inv: any) => (
                                                    <TableRow key={inv.id}>
                                                        <TableCell sx={{ whiteSpace: 'nowrap' }}>{new Date(inv.createdAt).toLocaleDateString()}</TableCell>
                                                        <TableCell sx={{ fontWeight: 600 }}>{inv.invoiceNo}</TableCell>
                                                        <TableCell sx={{ minWidth: 150 }}>{inv.items[0]?.description} {inv.items.length > 1 ? `(+${inv.items.length - 1} more)` : ''}</TableCell>
                                                        <TableCell align="right" sx={{ fontWeight: 700 }}>${Number(inv.totalAmount).toLocaleString()}</TableCell>
                                                        <TableCell align="center">
                                                            <Chip label={inv.status} size="small" variant="outlined" color={inv.status === 'PAID' ? 'success' : 'warning'} />
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                </Paper>
                            </Box>
                        )}
                    </Box>
                )}

                {tabValue === 6 && (
                    <Paper sx={{ p: isSmallMobile ? 2 : 3, borderRadius: '20px', border: '1px solid #edf2f7', boxShadow: 'none' }}>
                        <Typography variant="h6" fontWeight={700} mb={3}>Institutional Trial Balance</Typography>
                        <TableContainer sx={{ overflowX: 'auto' }}>
                            <Table size="small" sx={{ minWidth: isSmallMobile ? 600 : 'auto' }}>
                                <TableHead>
                                    <TableRow>
                                        <TableCell sx={{ fontWeight: 700 }}>Code</TableCell>
                                        <TableCell sx={{ fontWeight: 700 }}>Account Name</TableCell>
                                        <TableCell sx={{ fontWeight: 700 }}>Type</TableCell>
                                        <TableCell align="right" sx={{ fontWeight: 700 }}>Debit</TableCell>
                                        <TableCell align="right" sx={{ fontWeight: 700 }}>Credit</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {trialBalanceData.map((row: any, index: number) => (
                                        <TableRow key={index} hover>
                                            <TableCell>{row.code}</TableCell>
                                            <TableCell sx={{ fontWeight: 600 }}>{row.name}</TableCell>
                                            <TableCell><Chip label={row.type} size="small" sx={{ fontSize: '0.65rem' }} /></TableCell>
                                            <TableCell align="right" sx={{ color: '#2C5282' }}>{row.debit > 0 ? `$${row.debit.toLocaleString()}` : '-'}</TableCell>
                                            <TableCell align="right" sx={{ color: '#9B2C2C' }}>{row.credit > 0 ? `$${row.credit.toLocaleString()}` : '-'}</TableCell>
                                        </TableRow>
                                    ))}
                                    <TableRow sx={{ bgcolor: '#F7FAFC' }}>
                                        <TableCell colSpan={3} sx={{ fontWeight: 800 }}>TOTALS</TableCell>
                                        <TableCell align="right" sx={{ fontWeight: 900, color: '#2C5282' }}>
                                            ${trialBalanceData.reduce((sum, r) => sum + r.debit, 0).toLocaleString()}
                                        </TableCell>
                                        <TableCell align="right" sx={{ fontWeight: 900, color: '#9B2C2C' }}>
                                            ${trialBalanceData.reduce((sum, r) => sum + r.credit, 0).toLocaleString()}
                                        </TableCell>
                                    </TableRow>
                                </TableBody>
                            </Table>
                        </TableContainer>

                        <Box sx={{ mt: 3, p: 2, bgcolor: '#EBF8FF', borderRadius: '12px', border: '1px solid #BEE3F8', display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Box sx={{ p: 1, bgcolor: '#fff', borderRadius: '50%' }}>🛡️</Box>
                            <Typography variant="body2" color="#2A4365">
                                <strong>System Integrity Assurance:</strong> The Trial Balance is balanced. All debits match credits across the institutional ledger.
                            </Typography>
                        </Box>
                    </Paper>
                )}
            </Box>
        </Box>
    );
};

export default ReportsPage;
